import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ReportBuilderService } from '../../report-builder.service';
import { CsvExportUtils } from '../../../../shared/utils/csv-export-utils';
import { MomentDateUtils } from '../../../../shared/utils/moment-date-utils';
import { UnitByDayReportData } from './report-preset.model';
import { DateRangeType } from '../../../../shared/domain/date-range-type.enum';
import { DateRangeHolder } from '../../../../shared/domain/date-range-holder.model';
import { ActivatedRoute } from '@angular/router';
import { GlobalSpinnerService } from '../../../../shared/components/loader-service/global-spinner.service';
import { SelectionModel } from '@angular/cdk/collections';
import { StatusChangeLine } from '../../../shared/domain/alerts/status-change-line.model';
import { TeamSupportDialogComponent } from '../../../shared/components/team-support-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material';


@Component({
  selector: 'cm-report-presets-grouping-by-days',
  templateUrl: './report-presets-grouping-by-days.component.html',
  styleUrls: ['./report-presets-grouping-by-days.component.scss']
})
export class ReportPresetsGroupingByDaysComponent implements AfterViewInit, OnInit {
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  MomentDateUtils = MomentDateUtils;

  dataSource = new MatTableDataSource<UnitByDayReportData>();
  loading = false;
  selection = new SelectionModel<UnitByDayReportData>(true, []);

  columns = [];
  days: string[] = [];

  filter = {
    name: '',
    accountManagerEmail: '',
    salesRepresentativeEmail: '',
    nameCtrl: new FormControl(),
    adUnitIds: []
  };

  dateSelector = {
    dateFrom: this.MomentDateUtils.nDaysFromDate(MomentDateUtils.today(), -4),
    dateTo: MomentDateUtils.yesterday(),
    maxDate: MomentDateUtils.yesterday(),
    allowedDateRangeTypes: [
      DateRangeType.YESTERDAY,
      DateRangeType.THIS_WEEK,
      DateRangeType.LAST_7_DAYS,
      DateRangeType.LAST_MONTH,
      DateRangeType.LAST_30_DAYS
    ],
    onDateRangeChanged: (event: DateRangeHolder) => {
      this.dateSelector.dateFrom = event.dateFrom;
      this.dateSelector.dateTo = event.dateTo;
      this.refreshData();
    }
  };

  constructor(private service: ReportBuilderService, private matDialog: MatDialog,
              private route: ActivatedRoute, private gss: GlobalSpinnerService) {
    this.route.queryParams.subscribe(() => {
      const adUnitId = this.route.snapshot.queryParams['adUnitId'] || null;
      if (adUnitId) {
        this.filter.adUnitIds = [adUnitId];
      }
    });
  }

  ngOnInit(): void {
    this.refreshData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    const customFilterFn = (data: UnitByDayReportData, filter: string) => {
      const nameFilterOk = !this.filter.name || (
        data.publisherName.toLowerCase().includes(this.filter.name.toLowerCase())
        || (data.salesRepresentative && data.salesRepresentative.toLowerCase().includes(this.filter.name.toLowerCase()))
        || data.bridgeAdUnitName.toLowerCase().includes(this.filter.name.toLowerCase())
      );
      return nameFilterOk;
    };
    this.dataSource.filterPredicate = customFilterFn;

    this.filter.nameCtrl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged())
      .subscribe(next => {
        this.filter.name = next;
        this.filterData();
      });
  }

  filterData() {
    this.dataSource.filter = <string>(this.filter ? this.filter : '');
  }

  refreshData() {
    this.loading = true;

    this.service.presetUnitByDay(this.dateSelector.dateFrom, this.dateSelector.dateTo, this.filter.adUnitIds, this.filter.salesRepresentativeEmail, this.filter.accountManagerEmail).subscribe(result => {
      // const result = this.data;
      this.loading = false;
      result.data.forEach(data => {
        Object.keys(data.days).forEach(key => {
          data.days[key].ui_ioCpmRpmDiff = (data.days[key].rpm || 0) - (data.days[key].ioCpm || 0);
        });
      });

      // client side filter
      this.dataSource.data = result.data.filter(row => {
        let display = false;
        Object.keys(row.days).forEach(day => {
          if (row.days[day].ui_ioCpmRpmDiff < -0.10) {
            display = true;
          }
        });
        return display;
      });
      this.days = result.days;
      this.columns = ['select', 'salesRepresentative', 'publisherName', 'bridgeAdUnitName'].concat(result.days);
    }, error => {
      this.loading = false;
    });
  }

  exportData() {
    const dataToExport = this.dataSource.data.map(item => {
      const dataObject = {
        'Sales Representative': item.salesRepresentative,
        'Publisher Name': item.publisherName,
        'BridgeAdUnit Name': item.bridgeAdUnitName,
      };

      Object.keys(item.days).forEach(day => {
        dataObject[day + ' Impressions'] = item.days[day].impressions;
        dataObject[day + ' IOCPM'] = item.days[day].ioCpm;
        dataObject[day + ' RPM'] = item.days[day].rpm;
        dataObject[day + ' DIFF'] = item.days[day].ui_ioCpmRpmDiff;
      });

      return dataObject;
    });

    CsvExportUtils.exportCsv(dataToExport, 'Preset report-' + new Date() + '.csv');
  }

  exportUnitHbRevReport() {
    this.gss.showLoader();
    this.service.unitHbidRevReport(this.dateSelector.dateFrom, this.dateSelector.dateTo).subscribe(next => {
      CsvExportUtils.exportBlob([next], 'unit-hb-rev-report.csv');
      this.gss.hideLoader();
    }, () => {
      this.gss.hideLoader();
    });
  }

  getTotal(date: string, columnName: string) {
    return this.dataSource.data.reduce((sum, currentValue) => {
      const record = currentValue.days[date];

      if (!record) {
        return sum;
      }

      const value = Number(record[columnName]);
      const correctValue = isNaN(value) ? 0 : value;
      return sum + correctValue;
    }, 0);
  }

  onClickSendTeamEmail() {
    this.matDialog.open(TeamSupportDialogComponent, <MatDialogConfig>{
      data: {
        units: this.selection.selected.map(item => {
          return {
            id: item.bridgeAdUnitId,
            name: item.bridgeAdUnitName
          };
        })
      }
    }).afterClosed().subscribe(next => {
      if (next) {
        this.refreshData();
      }
    });
  }


  getAverage(date: string, columnName: string) {
    const total = this.getTotal(date, columnName);

    return (total / this.dataSource.data.length).toFixed(2);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }
  toggle(row) {
    this.selection.toggle(row);
  }
}
